// Matches the server's own normalization (server.js lowercases/trims every
// email before lookup/storage) - appState.currentUserEmail must be
// normalized the same way at every write site, since every "is this mine"
// check elsewhere in the app (chat/gifts/together/game) does a raw ===
// comparison against server-returned, already-lowercase emails. Mobile
// keyboards commonly auto-capitalize the first letter of a text/email
// input, so a raw, unnormalized value here is a real, not just
// theoretical, way for those comparisons to silently break for a session.
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

export function ext(name = ""): string {
  return name.toLowerCase().replace(".enc", "").split(".").pop() || "";
}

export function canPlayOnIOS(filename: string): boolean {
  return ext(filename) !== "webm";
}

export function isVideoFilename(name = ""): boolean {
  return ["mp4", "webm", "mov"].includes(ext(name));
}

// appState.selectedFilenames/favoritesSet are Set<string> mutated in place
// by the original vanilla code - but React's useSyncExternalStore compares
// snapshots by reference (Object.is), so an in-place .add()/.delete() on
// the same Set instance is invisible to it (opposite problem from the
// "selector returns a fresh array every call" gotcha - here the reference
// never changes even though the contents did). Always replace with a new
// Set instance when a React consumer needs to observe the change.
export function setWith<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  next.add(value);
  return next;
}

export function setWithout<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  next.delete(value);
  return next;
}

export function videoMime(filename = ""): string {
  const e = ext(filename);
  if (e === "mp4") return "video/mp4";
  if (e === "mov") return "video/quicktime";
  if (e === "webm") return "video/webm";
  return "video/mp4";
}

export function formatRelativeTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";

  const diffSec = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (diffSec < 60) return "now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  const diffWeek = Math.round(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek}w`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth}mo`;
  return `${Math.round(diffDay / 365)}y`;
}

// Sibling to formatRelativeTime, counting forward instead of back - used
// for time-locked gift countdowns ("unlocks in 3d").
export function formatFutureRelativeTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";

  const diffSec = Math.max(0, Math.round((ms - Date.now()) / 1000));
  if (diffSec < 60) return "in <1m";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `in ${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `in ${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `in ${diffDay}d`;
  const diffWeek = Math.round(diffDay / 7);
  if (diffWeek < 5) return `in ${diffWeek}w`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `in ${diffMonth}mo`;
  return `in ${Math.round(diffDay / 365)}y`;
}

// Distinct from formatFutureRelativeTime's coarser single-unit rounding
// ("in 2h") - Scheduled Watch Together's countdown wants two-unit
// precision under a day ("Starts in 2h 14m") and a friendly "tomorrow at"/
// weekday phrasing once a bare duration stops being the most useful thing
// to show.
export function formatWatchTogetherCountdown(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const diffMs = ms - Date.now();
  if (diffMs <= 0) return "Starting now";

  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `Starts in ${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  const remMin = diffMin % 60;
  if (diffHr < 24) return `Starts in ${diffHr}h${remMin > 0 ? ` ${remMin}m` : ""}`;

  const target = new Date(ms);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(target) - startOfDay(now)) / 86_400_000);
  const timeStr = target.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (dayDiff === 1) return `Starts tomorrow at ${timeStr}`;
  if (dayDiff > 1 && dayDiff <= 6) return `Starts ${target.toLocaleDateString(undefined, { weekday: "long" })} at ${timeStr}`;
  return `Starts ${target.toLocaleDateString(undefined, { month: "short", day: "numeric" })} at ${timeStr}`;
}

// mm:ss (h:mm:ss past an hour) - voice note recording/playback duration.
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${mins}:${pad(secs)}`;
}
